// src/components/Marketing/IDSCRMProjectMap.tsx
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

export default function IDSCRMProjectMap() {
    const [active, setActive] = useState<string | null>(null);

    const portals = [
        { name: "Public Portal", desc: "Marketing, About, and Dealer Network entry point.", color: "from-sky-400 to-sky-600", progress: 90 },
        { name: "Dealer Portal", desc: "Inventory, leads, finance, and floorplan control center.", color: "from-emerald-400 to-emerald-600", progress: 85 },
        { name: "Finance Portal", desc: "Credit applications, lender matching, and funding tools.", color: "from-indigo-400 to-indigo-600", progress: 80 },
        { name: "Customer Portal", desc: "Vehicle wishlists, trade-ins, and purchase tracking.", color: "from-amber-400 to-amber-600", progress: 70 },
        { name: "Service Portal", desc: "Repair orders, warranty tracking, and maintenance history.", color: "from-rose-400 to-rose-600", progress: 60 },
        { name: "Admin Portal", desc: "User management, reporting, analytics, and system setup.", color: "from-gray-400 to-gray-600", progress: 75 },
        { name: "Bank/Lender Portal", desc: "Funding dashboards and dealer-lender integrations.", color: "from-purple-400 to-purple-600", progress: 65 },
    ];

    const backend = [
        "API Layer (Express + Prisma)",
        "Database (MySQL / Prisma)",
        "Mailer (SMTP / Nodemailer)",
        "Socket.IO (Real-Time)",
        "Mediasoup (Video Streams)",
    ];

    const integrations = [
        "DMS: Frazer / DealerCenter",
        "Data: CARFAX / Manheim / KBB",
        "Marketing: Motor.AC / AutoCityMag",
        "Fintech: Goonie Pay / BlackWallStreet",
    ];

    return (
        <div className="w-full flex flex-col items-center py-20 space-y-12">
            <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl font-bold text-center"
            >
                IDSCRM System Map
            </motion.h2>

            {/* PORTALS */}
            <div className="flex flex-wrap justify-center gap-4 max-w-5xl">
                {portals.map((p) => (
                    <motion.button
                        key={p.name}
                        whileHover={{ scale: 1.05 }}
                        onClick={() => setActive(active === p.name ? null : p.name)}
                        className={`rounded-xl px-4 py-3 text-white text-sm font-semibold bg-gradient-to-br ${p.color} shadow-lg`}
                    >
                        {p.name}
                    </motion.button>
                ))}
            </div>

            {/* Active Info Panel */}
            <AnimatePresence>
                {active && (
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-card text-card-foreground border rounded-2xl p-6 shadow-xl w-[90%] md:w-[50%]"
                    >
                        {(() => {
                            const p = portals.find((x) => x.name === active)!;
                            return (
                                <div className="space-y-3 text-center">
                                    <h3 className="text-xl font-semibold">{p.name}</h3>
                                    <p className="text-muted-foreground text-sm">{p.desc}</p>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className="bg-primary h-2 rounded-full transition-all"
                                            style={{ width: `${p.progress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Progress: {p.progress}%
                                    </p>
                                </div>
                            );
                        })()}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* BACKEND */}
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
                {backend.map((s) => (
                    <motion.div
                        key={s}
                        whileHover={{ scale: 1.03 }}
                        className="rounded-xl border p-3 text-sm font-medium text-center bg-muted"
                    >
                        {s}
                    </motion.div>
                ))}
            </div>

            {/* ARROW */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-muted-foreground text-3xl"
            >
                ↓
            </motion.div>

            {/* INTEGRATIONS */}
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
                {integrations.map((i) => (
                    <motion.div
                        key={i}
                        whileHover={{ scale: 1.03 }}
                        className="rounded-xl border p-3 text-sm text-center font-medium bg-background shadow-sm"
                    >
                        {i}
                    </motion.div>
                ))}
            </div>

            <div className="pt-8 text-center text-sm text-muted-foreground max-w-2xl">
                <p>
                    IDSCRM connects every layer — from customers to lenders — through one
                    unified data and communication infrastructure.
                </p>
            </div>
        </div>
    );
}
